import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {
  useUploadSeedFile,
  useGetSeedPath,
  useSeedPageCount,
} from '../../../../../hooks/productions/useProductions';
import {ToastType} from '../../../../../shared/constants';
import ArrToast from '../../../../common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../../../assets/images/close-toast.svg';
import {t} from 'i18next';
import './ReplaceSeedPdfFlyout.scss';

interface ReplaceSeedPdfFlyoutProps {
  isOpen: boolean;
  documentId: number;
  productionId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const ReplaceSeedPdfFlyout: React.FC<ReplaceSeedPdfFlyoutProps> = ({
  isOpen,
  documentId,
  productionId,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [newSeedFilePath, setNewSeedFilePath] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Call getSeedPath API when flyout opens (background call)
  const {data: seedPathData} = useGetSeedPath(
    {
      documentId: documentId,
      productionId: productionId,
    },
    isOpen && !!documentId
  );

  // Store seed path for future API calls in this flow
  // The _seedFilePath will be used in future API calls within this component
  const _seedFilePath = seedPathData?.seedFilePath || '';
  const _pageCount = seedPathData?.pageCount || 0;

  // Upload seed file when file is selected (no success toast, only error toast)
  const uploadSeedFileMutation = useUploadSeedFile(
    (data) => {
      // Check if response indicates an error using success field
      if (data.success === false) {
        // Show error toast with message from response
        const errorMessage =
          data.message ||
          t(
            'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.uploadFailed'
          );
        setToastMessage(errorMessage);
        setToastStatus([ToastType.Error]);
        setShowToast(true);
        // Don't clear selected file on error - allow user to retry
        setNewSeedFilePath('');
      } else if (data.success === true && data.seedFilePath) {
        // Success - store the new seed file path for seedPageCount API
        setNewSeedFilePath(data.seedFilePath);
      }
      // Don't show success message for uploadSeedFile
    },
    (error) => {
      // Show error toast for uploadSeedFile (for actual HTTP errors)
      const errorMessage =
        error?.message ||
        t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.uploadFailed'
        );
      setToastMessage(errorMessage);
      setToastStatus([ToastType.Error]);
      setShowToast(true);
      // Don't clear selected file on error - allow user to retry
      setNewSeedFilePath('');
    }
  );

  // Seed page count mutation - called when Save PDF is confirmed
  const seedPageCountMutation = useSeedPageCount(
    (data) => {
      // Check if response indicates an error using success field
      if (data.success === false) {
        setToastMessage(data.message);
        setToastStatus([ToastType.Error]);
        setShowToast(true);
        // Don't clear newSeedFilePath on error - allow user to retry
      } else if (data.success === true) {
        // Show success message only after seedPageCount succeeds
        setToastMessage(data.message);
        setToastStatus([ToastType.Success]);
        setShowToast(true);
        onSuccess?.();
        onClose();
      }
    },
    (error) => {
      // Show error toast for seedPageCount (for actual HTTP errors)
      const errorMessage =
        error?.message || 'Failed to validate seed file page count';
      setToastMessage(errorMessage);
      setToastStatus([ToastType.Error]);
      setShowToast(true);
    }
  );

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setNewSeedFilePath(''); // Reset previous upload path
      // Call uploadSeedFile API when file is selected
      uploadSeedFileMutation.mutate(file);
    }
  };

  const handleSavePdf = () => {
    if (selectedFile) {
      setShowConfirmationModal(true);
    }
  };

  const handleConfirmContinue = () => {
    setShowConfirmationModal(false);
    // Call seedPageCount API when Save PDF is confirmed
    // Validate all required data is available
    // All validations passed, proceed with API call
    seedPageCountMutation.mutate({
      payload: {
        pagecount: _pageCount,
        seedFilePath: _seedFilePath,
        newSeedFilePath: newSeedFilePath,
        documentID: documentId,
        productionID: productionId,
      },
    });
  };

  const handleConfirmCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setNewSeedFilePath('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleClose = () => {
    handleCancel();
  };

  // Set z-index on the modal when flyout opens
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        // Find the drawer element first
        const drawerElement = document.querySelector(
          '.arr-panel.replace-seed-pdf-flyout'
        );
        if (drawerElement) {
          // Find the closest MUI Modal root
          const modalRoot = drawerElement.closest(
            '.MuiModal-root'
          ) as HTMLElement;
          if (modalRoot) {
            modalRoot.style.zIndex = '10000';
          }

          // Also set on the Drawer root
          const drawerRoot = drawerElement.closest(
            '.MuiDrawer-root'
          ) as HTMLElement;
          if (drawerRoot) {
            drawerRoot.style.zIndex = '10000';
          }
        }

        // Alternative: find all MUI Modal roots and check which one contains our drawer
        const allModals = document.querySelectorAll('.MuiModal-root');
        allModals.forEach((modal) => {
          if (modal.querySelector('.replace-seed-pdf-flyout')) {
            (modal as HTMLElement).style.zIndex = '10000';
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // Set z-index on toast notification to appear above flyout and modals
  // Since toast is rendered via portal to document.body, we need to ensure it has the highest z-index
  useEffect(() => {
    if (showToast) {
      const timeoutId = setTimeout(() => {
        // Find toast notification container (now in document.body due to portal)
        const toastContainer = document.querySelector(
          '.toast-notification__container'
        );
        if (toastContainer) {
          (toastContainer as HTMLElement).style.zIndex = '10002';
          (toastContainer as HTMLElement).style.position = 'fixed';
        }
        // Also target MUI Snackbar root
        const snackbarRoots = document.querySelectorAll('.MuiSnackbar-root');
        snackbarRoots.forEach((snackbar) => {
          (snackbar as HTMLElement).style.zIndex = '10002';
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showToast]);

  // Set z-index on the confirmation modal when it opens
  useEffect(() => {
    if (showConfirmationModal) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        // Find the confirmation modal element
        const modalElement = document.querySelector(
          '.replace-seed-pdf-confirmation-modal'
        );
        if (modalElement) {
          // Find the closest MUI Modal root
          const modalRoot = modalElement.closest(
            '.MuiModal-root'
          ) as HTMLElement;
          if (modalRoot) {
            modalRoot.style.zIndex = '10001';
          }

          // Also set on the ArrModal itself
          const arrModal = modalElement.closest('.arr-modal') as HTMLElement;
          if (arrModal) {
            arrModal.style.zIndex = '10001';
          }
        }

        // Alternative: find all MUI Modal roots and check which one contains our confirmation modal
        const allModals = document.querySelectorAll('.MuiModal-root');
        allModals.forEach((modal) => {
          if (modal.querySelector('.replace-seed-pdf-confirmation-modal')) {
            (modal as HTMLElement).style.zIndex = '10001';
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showConfirmationModal]);

  return (
    <>
      <ArrDrawer
        isOpen={isOpen}
        onClose={handleClose}
        showButton={false}
        showHeader={false}
        size="small"
        side="right"
        className="replace-seed-pdf-flyout"
        ariaLabel={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
        )}
      >
        {/* Custom Header */}
        <Box className="replace-seed-pdf-flyout__header">
          <Typography
            className="replace-seed-pdf-flyout__title typo-text headline3_size"
            data-testid={`replace-seed-pdf-flyout-title`}>
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
            )}
          </Typography>
          <Box className="replace-seed-pdf-flyout__header-actions">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              dataTestId="replace-seed-pdf-flyout-cancel-button"
            >
              {t(
                'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.cancel'
              )}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              onClick={handleSavePdf}
              disabled={
                !selectedFile ||
                !newSeedFilePath ||
                uploadSeedFileMutation.isPending ||
                seedPageCountMutation.isPending
              }
              dataTestId="save-pdf-button"
            >
              {t(
                'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.savePdf'
              )}
            </ArrButton>
          </Box>
        </Box>

        <Box className="replace-seed-pdf-flyout__content">
          {/* File Selection Section - Box design similar to TagsSection */}
          <Box className="replace-seed-pdf-flyout__file-section">
            <Box className="replace-seed-pdf-flyout__file-header">
              <Typography
                className="typo-text subhead5_size replace-seed-pdf-flyout__file-label"
                data-testid={`replace-seed-pdf-flyout-select-file`}>
                {t(
                  'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.selectFile'
                )}
              </Typography>
              <ArrButton
                variant="primary"
                size="small"
                className="replace-seed-pdf-flyout__select-file-btn typo-text buttonSmall_size"
                onClick={handleSelectFileClick}
                dataTestId="select-file-button"
              >
                {t(
                  'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.selectFile'
                )}
              </ArrButton>
            </Box>
            <Box className="replace-seed-pdf-flyout__file-display">
              {selectedFile ? (
                <Typography
                  className="typo-text body4_size replace-seed-pdf-flyout__file-name"
                  data-testid="file-display"
                >
                  {selectedFile.name}
                </Typography>
              ) : (
                <Typography
                  className="typo-text body4_size replace-seed-pdf-flyout__file-placeholder"
                  data-testid="file-display"
                >
                  {t(
                    'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.noFileChosen'
                  )}
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              style={{display: 'none'}}
              onChange={handleFileChange}
              data-testid="file-input"
            />
          </Box>
        </Box>
      </ArrDrawer>
      {showToast &&
        (() => {
          const currentToastType = toastStatus[0];
          let toastType: 'success' | 'error' | 'warning' = 'error';
          if (currentToastType === ToastType.Success) {
            toastType = 'success';
          } else if (currentToastType === ToastType.Warning) {
            toastType = 'warning';
          }
          const toastTitle =
            currentToastType === ToastType.Success
              ? t('common.success')
              : t('common.error');
          return ReactDOM.createPortal(
            <ArrToast
              id="replace-seed-pdf-toast"
              data-testid="toast-notification"
              title={toastTitle}
              message={toastMessage}
              type={toastType}
              onClose={() => {
                setShowToast(false);
              }}
              dismissable
              closeIcon={<CloseToast />}
              position="bottom-right"
            />,
            document.body
          );
        })()}
      {/* Confirmation Modal */}
      <ArrModal
        showModal={showConfirmationModal}
        onClose={handleConfirmCancel}
        headerTitle={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.title'
        )}
        headerTitleClassName="headline3_size"
        description={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.description'
        )}
        ariaLabel={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.title'
        )}
        ariaDescription={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.description'
        )}
        showClose={false}
        hideDefaultActions={true}
        className="replace-seed-pdf-confirmation-modal"
        dataTestId="replace-seed-pdf-confirmation-modal"
      >
        <Box className="replace-seed-pdf-confirmation-modal__buttons">
          <ArrButton
            variant="primary"
            size="large"
            onClick={handleConfirmContinue}
            disabled={
              !newSeedFilePath ||
              uploadSeedFileMutation.isPending ||
              seedPageCountMutation.isPending
            }
            loading={seedPageCountMutation.isPending}
            dataTestId="yes-continue-button"
          >
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.yesContinue'
            )}
          </ArrButton>
          <ArrButton
            variant="secondary"
            size="large"
            onClick={handleConfirmCancel}
            disabled={
              uploadSeedFileMutation.isPending ||
              seedPageCountMutation.isPending
            }
            dataTestId="no-cancel-button"
          >
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.noCancel'
            )}
          </ArrButton>
        </Box>
      </ArrModal>
    </>
  );
};

export default ReplaceSeedPdfFlyout;
